<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>
          <slot name="title">{{ $t("LIST_OFFERS.TITLE") }}</slot>
        </v-toolbar-title>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="configLoaded"
              ref="offers-table"
              height="600"
              name="offers-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="filters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{ method: tableItemsMethod }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template v-slot:top-header-actions>
                <v-btn
                  icon
                  color="indigo"
                  :loading="isExporting"
                  :disabled="isProcessing"
                  @click="exportOffers"
                >
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-file-excel</v-icon>
                    </template>
                    <span>Export to Excel</span>
                  </v-tooltip>
                </v-btn>
                <v-spacer></v-spacer>
              </template>
              <template
                v-for="(header, index) in allColumns"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span :key="index">
                  <span
                    v-if="header.value === 'options'"
                    class="d-flex align-center justify-center flex-nowrap"
                  >
                    <v-btn icon color="indigo" @click="openOfferModal(item.item.id)">
                      <v-icon dark>mdi-eye</v-icon>
                    </v-btn>
                  </span>
                  <v-switch
                    v-else-if="header.value === 'is_active'"
                    v-model="item.item.is_active"
                    :true-value="1"
                    :false-value="0"
                    hide-details
                    class="ma-0 pa-0 d-inline-flex"
                    @change="updateActive(item.item)"
                  ></v-switch>
                  <template v-else-if="isTagColumn(header.value) || isMappingColumn(header.value)">
                    {{ dynamicValue(item.item, header.value) }}
                  </template>
                  <template v-else>
                    {{ item.value != null && item.value !== "" ? item.value : "-" }}
                  </template>
                </span>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <OfferModal
      :open="offerModalOpen"
      :offer-id="offerModalId"
      @close="closeOfferModal"
    />
  </v-card>
</template>

<script>
import moment from "moment";
import Table from "@/views/partials/v-table/Table.vue";
import OfferModal from "@/views/pages/offer/partials/OfferModal.vue";
import {
  GET_VENDOR_OFFERS,
  GET_VENDOR_OFFERS_TABLE_CONFIG,
  GET_VENDOR_OFFERS_TABLE_FILTERS,
  EXPORT_VENDOR_OFFERS,
  UPDATE_VENDOR_OFFER,
} from "@/store/vendorOffer.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";

export default {
  name: "OffersTable",
  components: {
    Table,
    OfferModal,
  },
  data() {
    return {
      tab: "tab-1",
      tableName: "offers-table",
      tableItemsMethod: GET_VENDOR_OFFERS,
      configLoaded: false,
      isProcessing: false,
      isExporting: false,
      blinkRowItemKey: null,
      headers: [],
      hiddenHeaders: [],
      filters: [],
      offerModalOpen: false,
      offerModalId: null,
    };
  },
  computed: {
    allColumns() {
      return [...this.headers, ...this.hiddenHeaders];
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    this.loadConfig();
    this.loadFilters();
  },
  methods: {
    loadConfig() {
      this.isProcessing = true;

      this.$store
        .dispatch(GET_VENDOR_OFFERS_TABLE_CONFIG)
        .then((config) => {
          this.headers = config.headers || [];
          this.hiddenHeaders = config.hiddenHeaders || [];

          this.seedDefaultFilters();
          this.configLoaded = true;
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    loadFilters() {
      this.$store
        .dispatch(GET_VENDOR_OFFERS_TABLE_FILTERS)
        .then((filters) => {
          this.filters = filters;
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    seedDefaultFilters() {
      const filtersKey = `table-state.${this.tableName}.serverFilters`;
      const existing = userStateService.getState(filtersKey, null);

      if (existing !== null) {
        return;
      }

      userStateService.setState(filtersKey, {
        is_active: [1],
        expire_date_from: moment().format("YYYY-MM-DD"),
      });
    },
    isTagColumn(value) {
      return typeof value === "string" && /^tag_\d+$/.test(value);
    },
    isMappingColumn(value) {
      return typeof value === "string" && /^mapping_\d+$/.test(value);
    },
    dynamicValue(item, headerValue) {
      let raw = null;

      if (this.isTagColumn(headerValue)) {
        const id = headerValue.substring("tag_".length);
        raw = item.tags ? item.tags[id] : null;
      } else if (this.isMappingColumn(headerValue)) {
        const id = headerValue.substring("mapping_".length);
        raw = item.mapping_values ? item.mapping_values[id] : null;
      }

      if (raw === null || raw === undefined || raw === "") {
        return "-";
      }

      if (typeof raw === "object") {
        if (raw.formatted_value !== undefined) return raw.formatted_value;
        if (raw.value !== undefined) return raw.value;
        return "-";
      }

      return raw;
    },
    updateActive(row) {
      this.$store
        .dispatch(UPDATE_VENDOR_OFFER, {
          id: row.id,
          params: { is_active: row.is_active },
        })
        .catch(() => {
          row.is_active = row.is_active ? 0 : 1;
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    openOfferModal(id) {
      if (!id) {
        return;
      }

      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.offerModalId = id;
      this.offerModalOpen = true;
    },
    closeOfferModal() {
      this.offerModalOpen = false;
      this.offerModalId = null;
    },
    exportOffers() {
      const options = userStateService.getState(`table-state.${this.tableName}.options`, {});

      this.isExporting = true;

      this.$store.dispatch(SHOW_SNACK, {
        message: "Export started. Please wait...",
        type: "warning",
        timeout: 10000,
      });

      this.$store
        .dispatch(EXPORT_VENDOR_OFFERS, {
          search: userStateService.getState(`table-state.${this.tableName}.searchString`, ""),
          filters: userStateService.getState(`table-state.${this.tableName}.serverFilters`, {}),
          sortBy: options.sortBy || [],
          sortDesc: options.sortDesc || [],
        })
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: "Export completed.",
            type: "success",
          });
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isExporting = false;
        });
    },
  },
};
</script>
